import { useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Container,
  Stack,
  FormControl,
  useTheme,
  Button,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { useAuthState } from 'state/useAuthState';
import { ANALYTICS_EVENTS, logEvent } from 'shared/analytics';
import { isProductionEnvironment } from 'shared/envUtils';
import { useQueryParams } from 'hooks/useQueryParms';
import RadioCard from './RadioCard';
import {
  MEMBERSHIP_PLANS,
  MEMBERSHIP_PLANS_AMOUNTS,
} from '../../../../constants';
import { useMembershipPlan } from '../MembershipPlanProvider';
import { ExpandedContent } from './ExpandedContent';
import { CardContent } from './CardContent';
import { ExpiryInfo, ManageMembershipButton } from './ManageMembership';
import { QueryParams } from './SupportUsContent.types';

const useStyles = makeStyles()((theme) => ({
  paper: {
    paddingTop: 20,
    paddingBottom: 20,
    overflowY: 'auto',
    width: '100%',
    backgroundColor: theme.palette.background.membership,
  },
}));

export const SupportUsContent = (): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [{ plan: selectedPlan }, setParams] = useQueryParams<QueryParams>();
  const { verifyingToken } = useMembershipPlan();
  const { isAuthenticated, hasActiveMembership, isProMember } = useAuthState();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();

  useEffect(() => {
    if (
      !selectedPlan ||
      [MEMBERSHIP_PLANS.yearly, 'donate'].includes(selectedPlan)
    ) {
      return;
    }

    const centerCard = (): void => {
      setTimeout(() => {
        if (!containerRef.current) return;

        const selectedCard = containerRef.current.querySelector(
          '.MuiCard-root[disabled]',
        );

        if (!selectedCard) return;

        const cardRect = selectedCard.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();

        const cardCenter = cardRect.top + cardRect.height / 2;
        const containerCenter = containerRect.top + containerRect.height / 2;
        const scrollOffset = cardCenter - containerCenter;

        const currentScroll = containerRef.current.scrollTop;
        containerRef.current.scrollTo({
          top: currentScroll + scrollOffset,
          behavior: 'smooth',
        });
      }, theme.transitions.duration.standard);
    };

    centerCard();
  }, [isProMember, selectedPlan, theme.transitions.duration.standard]);

  if (verifyingToken) {
    return <LoadingAndErrors isLoading />;
  }

  return (
    <Paper elevation={0} className={classes.paper} ref={containerRef}>
      <Container maxWidth="sm">
        <Stack
          spacing={3}
          sx={{
            alignItems: 'center',
          }}
        >
          <Typography
            align="center"
            sx={{
              fontSize: 16,
            }}
          >
            <Trans i18nKey="supportUs.description">
              As a non-profit, all critical safety features will be
              <em>free</em>, <b>forever</b>. Your financial contribution helps
              ensure free access for all.
            </Trans>
          </Typography>

          <FormControl fullWidth>
            <Stack spacing={3}>
              {!isProMember && (
                <RadioCard
                  title={t('supportUs.membership.title')}
                  value={MEMBERSHIP_PLANS.yearly}
                  onChange={(value) => {
                    setParams({ plan: value });
                  }}
                  checked={selectedPlan === MEMBERSHIP_PLANS.yearly}
                  active={hasActiveMembership}
                >
                  <RadioCard.Content>
                    {hasActiveMembership ? (
                      <ExpiryInfo />
                    ) : (
                      <CardContent
                        plan={MEMBERSHIP_PLANS.yearly}
                        description={t('supportUs.membership.description')}
                        amount={
                          MEMBERSHIP_PLANS_AMOUNTS[MEMBERSHIP_PLANS.yearly]
                        }
                      />
                    )}
                  </RadioCard.Content>
                  <RadioCard.CollapseContent>
                    {hasActiveMembership ? (
                      <ManageMembershipButton />
                    ) : (
                      <ExpandedContent plan={MEMBERSHIP_PLANS.yearly} />
                    )}
                  </RadioCard.CollapseContent>
                </RadioCard>
              )}

              <RadioCard
                title={t('supportUs.proMembership.title')}
                value={MEMBERSHIP_PLANS.proYearly}
                onChange={(value) => {
                  setParams({ plan: value });
                }}
                checked={
                  selectedPlan === MEMBERSHIP_PLANS.proYearly ||
                  selectedPlan === MEMBERSHIP_PLANS.proMonthly
                }
                active={isProMember}
              >
                <RadioCard.Content>
                  {isProMember ? (
                    <ExpiryInfo />
                  ) : (
                    <CardContent
                      plan={MEMBERSHIP_PLANS.proYearly}
                      description={t('supportUs.proMembership.description')}
                      amount={
                        MEMBERSHIP_PLANS_AMOUNTS[MEMBERSHIP_PLANS.proYearly]
                      }
                    />
                  )}
                </RadioCard.Content>
                <RadioCard.CollapseContent>
                  {isProMember ? (
                    <ManageMembershipButton />
                  ) : (
                    <ExpandedContent
                      plan={MEMBERSHIP_PLANS.proYearly}
                      upgrade={hasActiveMembership}
                    />
                  )}
                </RadioCard.CollapseContent>
              </RadioCard>

              {!isProductionEnvironment() && (
                <RadioCard
                  title={t('supportUs.teams.title')}
                  value="teams"
                  onChange={(value) => {
                    logEvent({ name: ANALYTICS_EVENTS.CLICKED.TEAMS });
                    setParams({ plan: value });
                  }}
                  checked={selectedPlan === 'teams'}
                >
                  <RadioCard.Content>
                    <CardContent
                      description={t('supportUs.teams.description')}
                    />
                  </RadioCard.Content>
                  <RadioCard.CollapseContent>
                    <ExpandedContent plan="teams" />
                  </RadioCard.CollapseContent>
                </RadioCard>
              )}

              <RadioCard
                title={t('supportUs.donate.title')}
                value="donate"
                onChange={(value) => {
                  logEvent({ name: ANALYTICS_EVENTS.CLICKED.DONATION });
                  setParams({ plan: value });
                }}
                checked={selectedPlan === 'donate'}
              >
                <RadioCard.Content>
                  <CardContent
                    description={t('supportUs.donate.description')}
                  />
                </RadioCard.Content>
                <RadioCard.CollapseContent>
                  <ExpandedContent />
                </RadioCard.CollapseContent>
              </RadioCard>
            </Stack>
          </FormControl>

          {!isAuthenticated && (
            <Button
              fullWidth
              size="large"
              component={Link}
              to="/login"
              variant="outlined"
              color="inherit"
            >
              {t('supportUs.alreadyAMember')}
            </Button>
          )}
        </Stack>
      </Container>
    </Paper>
  );
};
